// components/ui/Select.jsx - Shadcn style Select
import React, { useState, useRef, useEffect } from "react";
import { ChevronDown } from "lucide-react";



import Icon from "../AppIcon";

const Select = React.forwardRef(({
    className,
    options = [],
    value,
    defaultValue,
    placeholder = "Select an option",
    multiple = false,
    disabled = false,
    required = false,
    label,
    description,
    error,
    searchable = false,
    clearable = false,
    loading = false,
    id,
    name,
    onChange,
    onOpenChange,
    ...props
}, ref) => {
    const [isOpen, setIsOpen] = useState(false);
    const [searchTerm, setSearchTerm] = useState("");
    const [focusedIndex, setFocusedIndex] = useState(-1);
    const selectRef = useRef(null);
    const listboxRef = useRef(null);

    // Generate unique ID if not provided
    const selectId = id || `select-${Math.random()?.toString(36)?.substr(2, 9)}`;
    const errorId = `${selectId}-error`;
    const descId = `${selectId}-description`;
    const listboxId = `${selectId}-listbox`;

    const describedByIds = [
        error ? errorId : null,
        description ? descId : null
    ]?.filter(Boolean)?.join(' ');

    const selectedOption = options?.find(opt => opt?.value === value);

    useEffect(() => {
        const handleClickOutside = (event) => {
            if (selectRef?.current && !selectRef?.current?.contains(event?.target)) {
                setIsOpen(false);
            }
        };

        document?.addEventListener('mousedown', handleClickOutside);
        return () => document?.removeEventListener('mousedown', handleClickOutside);
    }, []);

    // Filter options based on search
    const filteredOptions = searchable && searchTerm
        ? options?.filter(option =>
            option?.label?.toLowerCase()?.includes(searchTerm?.toLowerCase()) ||
            (option?.value && option?.value?.toString()?.toLowerCase()?.includes(searchTerm?.toLowerCase()))
        )
        : options;

    // Get selected option(s) for display
    const getSelectedDisplay = () => {
        if (!value) return placeholder;

        if (multiple) {
            const selectedOptions = options?.filter(opt => value?.includes(opt?.value));
            if (selectedOptions?.length === 0) return placeholder;
            if (selectedOptions?.length === 1) return selectedOptions?.[0]?.label;
            return `${selectedOptions?.length} items selected`;
        }

        const selectedOption = options?.find(opt => opt?.value === value);
        return selectedOption ? selectedOption?.label : placeholder;
    };

    const handleToggle = () => {
        if (!disabled) {
            const newIsOpen = !isOpen;
            setIsOpen(newIsOpen);
            onOpenChange?.(newIsOpen);
            if (!newIsOpen) {
                setSearchTerm("");
            }
        }
    };

    const handleOptionSelect = (option) => {
        if (multiple) {
            const newValue = value || [];
            const updatedValue = newValue?.includes(option?.value)
                ? newValue?.filter(v => v !== option?.value)
                : [...newValue, option?.value];
            onChange?.(updatedValue);
        } else {
            onChange?.(option?.value);
            setIsOpen(false);
            onOpenChange?.(false);
        }
    };

    const handleClear = (e) => {
        e?.stopPropagation();
        onChange?.(multiple ? [] : '');
    };

    const handleSearchChange = (e) => {
        setSearchTerm(e?.target?.value);
    };

    const isSelected = (optionValue) => {
        if (multiple) {
            return value?.includes(optionValue) || false;
        }
        return value === optionValue;
    };

    const hasValue = multiple ? value?.length > 0 : value !== undefined && value !== '';

    const getSelectClasses = () => {
        let classes = 'flex items-center justify-between w-full rounded-md border bg-background px-3 py-2 text-sm ring-offset-background focus:outline-none focus:ring-2 focus:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50 cursor-pointer transition-all duration-200';
        
        if (error) {
            classes += ' border-red-500 focus:ring-red-500';
        } else {
            classes += ' border-input focus:ring-ring';
        }
        
        return `${classes} ${className}`;
    };

    const handleKeyDown = (e) => {
        if (disabled) return;
        switch (e?.key) {
            case 'Enter': 
            case ' ':
                e?.preventDefault();
                if (isOpen && focusedIndex >= 0) {
                    onChange?.(options?.[focusedIndex]?.value);
                    setIsOpen(false);
                    setFocusedIndex(-1);
                } else {
                    setIsOpen(prev => !prev);
                }
                break;
            case 'ArrowDown':
                e?.preventDefault();
                if (!isOpen) {
                    setIsOpen(true);
                    setFocusedIndex(0);
                } else {
                    setFocusedIndex(prev => Math.min(prev + 1, options?.length - 1));
                }
                break;
            case 'ArrowUp':
                e?.preventDefault();
                setFocusedIndex(prev => Math.max(prev - 1, 0));
                break;
            case 'Escape':
                e?.preventDefault();
                setIsOpen(false);
                setFocusedIndex(-1);
                break;
            case 'Tab':
                setIsOpen(false);
                setFocusedIndex(-1);
                break;
            default:
                break;
        }
    };

    const triggerClasses = [
        'flex w-full items-center justify-between rounded-md border bg-background px-3 py-2 text-sm',
        'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-offset-2',
        'disabled:cursor-not-allowed disabled:opacity-50 transition-all duration-200',
        error ? 'border-red-500 focus-visible:ring-red-500' : 'border-input focus-visible:ring-ring'
    ]?.join(' ');

    return (
        <div className="space-y-1.5" ref={selectRef}>
            {label && (
                <label
                    id={`${selectId}-label`}
                    htmlFor={selectId}
                    className="text-sm font-medium text-foreground flex items-center gap-1"
                >
                    {label}
                    {required && <span className="text-red-500" aria-hidden="true">*</span>}
                </label>
            )}
            <div className="relative">
                <button
                    id={selectId}
                    type="button"
                    role="combobox"
                    aria-label={label || placeholder}
                    aria-required={required ? 'true' : undefined}
                    aria-invalid={error ? 'true' : 'false'}
                    aria-expanded={isOpen}
                    aria-haspopup="listbox"
                    aria-controls={listboxId}
                    aria-activedescendant={focusedIndex >= 0 ? `${selectId}-option-${focusedIndex}` : undefined}
                    aria-describedby={describedByIds || undefined}
                    disabled={disabled}
                    className={triggerClasses}
                    onClick={() => !disabled && setIsOpen(prev => !prev)}
                    onKeyDown={handleKeyDown}
                >
                    <span className={selectedOption ? 'text-foreground' : 'text-muted-foreground'}>
                        {selectedOption?.label || placeholder}
                    </span>
                    <Icon
                        name={isOpen ? 'ChevronUp' : 'ChevronDown'}
                        size={16}
                        color="var(--color-muted-foreground)"
                        aria-hidden="true"
                    />
                </button>

                {/* Hidden native select for form submission */}
                <select
                    name={name}
                    value={value || ''}
                    onChange={() => { }} // Controlled by our custom logic
                    className="sr-only"
                    tabIndex={-1}
                    multiple={multiple}
                    required={required}
                >
                    <option value="">Select...</option>
                    {options?.map(option => (
                        <option key={option?.value} value={option?.value}>
                            {option?.label}
                        </option>
                    ))}
                </select>

                {isOpen && (
                    <ul
                        ref={listboxRef}
                        id={listboxId}
                        role="listbox"
                        aria-label={label || placeholder}
                        aria-labelledby={label ? `${selectId}-label` : undefined}
                        className="absolute z-50 w-full mt-1 bg-card border border-border rounded-md shadow-lg max-h-60 overflow-y-auto"
                    >
                        {options?.map((option, index) => (
                            <li
                                key={option?.value}
                                id={`${selectId}-option-${index}`}
                                role="option"
                                aria-selected={option?.value === value}
                                className={[
                                    'px-3 py-2 text-sm cursor-pointer transition-colors',
                                    option?.value === value ? 'bg-primary/10 text-primary font-medium' : 'text-foreground hover:bg-muted',
                                    focusedIndex === index ? 'bg-muted' : ''
                                ]?.join(' ')}
                                onClick={() => {
                                    onChange?.(option?.value);
                                    setIsOpen(false);
                                    setFocusedIndex(-1);
                                }}
                                onMouseEnter={() => setFocusedIndex(index)}
                            >
                                {option?.label}
                                {option?.description && (
                                    <span className="block text-xs text-muted-foreground mt-0.5">{option?.description}</span>
                                )}
                            </li>
                        ))}
                    </ul>
                )}
            </div>
            {error && (
                <p
                    id={errorId}
                    className="text-xs text-red-500 flex items-center gap-1"
                    role="alert"
                    aria-live="assertive"
                    aria-atomic="true"
                >
                    <Icon name="AlertCircle" size={12} color="#dc2626" strokeWidth={2} aria-hidden="true" />
                    {error}
                </p>
            )}
            {!error && description && (
                <p id={descId} className="text-xs text-muted-foreground">
                    {description}
                </p>
            )}
        </div>
    );
});

Select.displayName = "Select";

export default Select;